<template>
    <div class="max-w-(--breakpoint-md) flex flex-col gap-4">
        <page-header>Terms &amp; Conditions</page-header>

        <page-loading v-if="loading" />

        <empty-state v-else-if="!term" data-test="terms-empty-state">
            No Terms &amp; Conditions are in place for this team. There's nothing to review or accept right now.
        </empty-state>

        <template v-else>
            <vx-alert
                v-if="accepted"
                data-test="terms-accepted-alert"
                color="success"
                :closeable="false"
                :shadow="true"
            >
                <template v-if="acceptance && owner">
                    Accepted on <strong>{{ formattedAcceptedAt }}</strong> by <strong>{{ owner.name }}</strong
                    >.
                </template>
                <template v-else> Accepted. </template>
            </vx-alert>

            <vx-alert
                v-else-if="isOwner"
                data-test="terms-pending-owner-alert"
                color="info"
                :closeable="false"
                :shadow="true"
            >
                <div class="font-extrabold text-base mb-1">Action required</div>

                <div v-if="hasTakenEffect">
                    New Terms &amp; Conditions are now in effect. You as the group Owner must accept these terms to
                    restore service.
                </div>
                <div v-else>
                    New Terms &amp; Conditions take effect on <strong>{{ formattedEffectiveAt }}</strong
                    >. You as the group Owner must accept these terms to avoid interruptions in service.
                </div>
            </vx-alert>

            <vx-alert v-else data-test="terms-pending-non-owner-alert" color="info" :closeable="false" :shadow="true">
                <div>
                    <template v-if="hasTakenEffect">These terms are now in effect.</template>
                    <template v-else
                        >These terms take effect on <strong>{{ formattedEffectiveAt }}</strong
                        >.</template
                    >
                    <template v-if="owner">
                        Only your group owner, <strong>{{ owner.name }}</strong
                        >, can accept them on your group's behalf.
                    </template>
                    <template v-else> Only your group owner can accept them on your group's behalf. </template>
                </div>
                <template v-if="owner" v-slot:right>
                    <vx-button data-test="terms-contact-owner-link" color="info" :href="`mailto:${owner.email}`">
                        Contact {{ owner.name }}
                    </vx-button>
                </template>
            </vx-alert>

            <vx-expandable-card>
                <template v-slot:header>
                    <span class="text-3xl font-extrabold text-slate-700">{{ term.title }}</span>
                </template>
                <template v-slot:content>
                    <markdown-renderer :source="term.content" data-test="terms-content" />
                </template>
            </vx-expandable-card>

            <template v-if="!accepted && isOwner">
                <vx-box color="info" class="px-6 py-4 flex items-center gap-4" data-test="terms-consent-panel">
                    <vx-checkbox v-model="consentChecked" data-test="terms-consent-checkbox" />
                    <label class="text-sm cursor-pointer grow" @click="consentChecked = !consentChecked">
                        {{ term.consent_text }}
                    </label>
                </vx-box>
                <vx-popover class="grow w-full" arrow placement="top" hover>
                    <vx-button
                        block
                        size="xl"
                        color="primary"
                        :disabled="!consentChecked"
                        :loading="submitting || loading"
                        data-test="terms-submit-button"
                        @click="submit"
                    >
                        {{ term.consent_button }}
                    </vx-button>
                    <template v-if="!consentChecked" v-slot:content> Tick the box to enable accepting. </template>
                </vx-popover>
            </template>
        </template>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { VxAlert, VxBox, VxButton, VxCheckbox, VxExpandableCard, VxPopover } from '@voxie/frontend-components';
import EmptyState from '~/components/general/EmptyState.vue';
import MarkdownRenderer from '~/components/general/MarkdownRenderer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import termsClient from '~/services/termsClient';
import type { GroupOwnerContact, Term, TermAcceptance } from '~/services/termsClient.types';
import { shortDate } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { catchRequestError } from '~/utils/request';
import { useCallout } from '~/composables/useCallout';

const props = defineProps<{
    teamId: number;
    groupId: string;
}>();

const toasted = useToasted();
const isOwner = Spark.roleOnCurrentGroup === 'owner';
const callout = useCallout();

const loading = ref(true);
const term = ref<Term | null>(null);
const accepted = ref(false);
const owner = ref<GroupOwnerContact | null>(null);
const acceptance = ref<TermAcceptance | null>(null);
const consentChecked = ref(false);
const submitting = ref(false);

const formattedEffectiveAt = computed(() => (term.value ? shortDate(term.value.effective_at) : ''));
const formattedAcceptedAt = computed(() => (acceptance.value ? shortDate(acceptance.value.accepted_at) : ''));
const hasTakenEffect = computed(() => (term.value ? dayjs.utc(term.value.effective_at).isBefore(dayjs()) : false));

const loadActive = async ({ showLoading = true }: { showLoading?: boolean } = {}) => {
    if (showLoading) {
        loading.value = true;
    }
    try {
        const { data } = await termsClient.getActiveTerm(props.teamId, props.groupId);
        term.value = data.term;
        accepted.value = data.accepted;
        owner.value = data.owner;
        acceptance.value = data.acceptance;
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
        term.value = null;
        accepted.value = false;
        owner.value = null;
        acceptance.value = null;
    } finally {
        loading.value = false;
    }
};

const submit = async () => {
    if (!term.value || !isOwner || !consentChecked.value) {
        return;
    }

    submitting.value = true;

    try {
        await termsClient.acceptTerm(props.teamId, props.groupId, term.value.id);
        consentChecked.value = false;
        if (Spark.state.currentGroup?.is_billable) {
            callout.danger(
                'group-billing',
                '<strong>ADDITIONAL ACTION REQUIRED:</strong> Enter billing email, address, and credit card information below. Account may be suspended for missing billing information.',
                {
                    ttl: 30,
                    action: Spark.stripeKey
                        ? { label: 'Add Payment Method', scrollTo: '#group-payment-method' }
                        : undefined,
                }
            );
            // callouts render newest-first, so the success alert shows above the action-required one
            callout.success('group-billing', 'Terms & Conditions successfully accepted!', { ttl: 30 });
            window.location.assign('/settings/group-billing#/billing-details');
            return;
        }
        await loadActive({ showLoading: false });
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        submitting.value = false;
    }
};

onMounted(() => {
    loadActive();
});
</script>
